import { Fragment, useMemo } from 'react'

import {
  DocumentBody,
  DocumentHeading,
  DocumentParagraph,
  DocumentSubheading,
  PolicyList,
  PolicyTable,
  TableScroll,
} from '../pages/PrivacyPolicy.styles'

const INLINE_PATTERN = /(`[^`]+`|\[[^\]]+\]\([^)]+\))/g

function isSafeDocumentUrl(url) {
  return /^(https?:|mailto:)/i.test(url)
}

function renderInline(text) {
  return text.split(INLINE_PATTERN).map((part, index) => {
    const codeMatch = part.match(/^`(.+)`$/)
    if (codeMatch) return <code key={`${part}-${index}`}>{codeMatch[1]}</code>

    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (linkMatch) {
      if (!isSafeDocumentUrl(linkMatch[2])) {
        return <Fragment key={`${part}-${index}`}>{linkMatch[1]}</Fragment>
      }
      return (
        <a key={`${part}-${index}`} href={linkMatch[2]} target="_blank" rel="noreferrer">
          {linkMatch[1]}
        </a>
      )
    }

    return <Fragment key={`${part}-${index}`}>{part}</Fragment>
  })
}

function createHeadingId(text) {
  return `section-${text
    .toLowerCase()
    .replace(/[^0-9a-z가-힣]+/g, '-')
    .replace(/^-|-$/g, '')}`
}

function parseDocument(content) {
  const lines = content.trim().split('\n')
  const blocks = []

  for (let index = 0; index < lines.length;) {
    const line = lines[index].trim()

    if (!line) {
      index += 1
      continue
    }

    if (line.startsWith('## ') || line.startsWith('### ')) {
      const level = line.startsWith('### ') ? 3 : 2
      const text = line.slice(level + 1)
      blocks.push({ type: 'heading', level, text, id: createHeadingId(text) })
      index += 1
      continue
    }

    if (line.startsWith('|') && lines[index + 1]?.trim().match(/^\|(?:\s*:?-+:?\s*\|)+$/)) {
      const rows = []
      const parseRow = (row) => row.slice(1, -1).split('|').map((cell) => cell.trim())
      rows.push(parseRow(line))
      index += 2

      while (index < lines.length && lines[index].trim().startsWith('|')) {
        rows.push(parseRow(lines[index].trim()))
        index += 1
      }

      blocks.push({ type: 'table', headers: rows[0], rows: rows.slice(1) })
      continue
    }

    if (line.startsWith('- ')) {
      const items = []
      while (index < lines.length && lines[index].trim().startsWith('- ')) {
        items.push(lines[index].trim().slice(2))
        index += 1
      }
      blocks.push({ type: 'list', items })
      continue
    }

    const paragraph = [line]
    index += 1
    while (
      index < lines.length
      && lines[index].trim()
      && !lines[index].trim().startsWith('## ')
      && !lines[index].trim().startsWith('### ')
      && !lines[index].trim().startsWith('|')
      && !lines[index].trim().startsWith('- ')
    ) {
      paragraph.push(lines[index].trim())
      index += 1
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') })
  }

  return blocks
}

export function getPolicySections(content) {
  return parseDocument(content).filter(({ type, level }) => type === 'heading' && level === 2)
}

export default function PrivacyPolicyDocument({ content }) {
  const blocks = useMemo(() => parseDocument(content), [content])

  return (
    <DocumentBody>
      {blocks.map((block, blockIndex) => {
        if (block.type === 'heading') {
          const Heading = block.level === 2 ? DocumentHeading : DocumentSubheading
          return <Heading id={block.id} key={block.id}>{block.text}</Heading>
        }

        if (block.type === 'table') {
          return (
            <TableScroll key={`table-${blockIndex}`} tabIndex="0">
              <PolicyTable>
                <thead>
                  <tr>
                    {block.headers.map((header) => <th key={header}>{renderInline(header)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={`row-${rowIndex}`}>
                      {row.map((cell, cellIndex) => (
                        <td key={`${cellIndex}-${cell}`}>{renderInline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </PolicyTable>
            </TableScroll>
          )
        }

        if (block.type === 'list') {
          return (
            <PolicyList key={`list-${blockIndex}`}>
              {block.items.map((item) => <li key={item}>{renderInline(item)}</li>)}
            </PolicyList>
          )
        }

        return (
          <DocumentParagraph key={`paragraph-${blockIndex}`}>
            {renderInline(block.text)}
          </DocumentParagraph>
        )
      })}
    </DocumentBody>
  )
}
